import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTierConfig } from '../services/api';
import type { TierConfigResponse, TierLimitConfig, Tier, TierFeatureGates } from '../types';

interface TierConfigContextValue {
  tierConfig: TierConfigResponse | undefined;
  isLoading: boolean;
  /** Get limits for a specific tier. Returns undefined while loading. */
  getLimits: (tier: Tier) => TierLimitConfig | undefined;
  /** Map of feature key → minimum tier that unlocks it. Returns empty object while loading. */
  minTierPerFeature: Partial<Record<keyof TierFeatureGates, Tier>>;
}

const TierConfigContext = createContext<TierConfigContextValue>({
  tierConfig: undefined,
  isLoading: true,
  getLimits: () => undefined,
  minTierPerFeature: {},
});

/**
 * TierConfigProvider — fetches tier limits from GET /api/config/tiers at app
 * boot (before any page renders) and makes them available via useTierConfig().
 *
 * This is the single source of truth for tier limits in the frontend.
 * All per-tier product limits, LLM caps, history windows, and feature gates
 * are served from the backend rather than duplicated as frontend constants.
 */
export function TierConfigProvider({ children }: Readonly<{ children: React.ReactNode }>): React.ReactElement {
  const { data: tierConfig, isLoading } = useQuery<TierConfigResponse>({
    queryKey: ['config', 'tiers'],
    queryFn: getTierConfig,
    // Config is stable — cache for the entire session
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 3,
  });

  const getLimits = (tier: Tier): TierLimitConfig | undefined => {
    return tierConfig?.tiers[tier];
  };

  const minTierPerFeature = tierConfig?.minTierPerFeature ?? {};

  const value = useMemo(
    () => ({ tierConfig, isLoading, getLimits, minTierPerFeature }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tierConfig, isLoading],
  );

  return (
    <TierConfigContext.Provider value={value}>
      {children}
    </TierConfigContext.Provider>
  );
}

/** Access tier config loaded at app boot. */
export function useTierConfig(): TierConfigContextValue {
  return useContext(TierConfigContext);
}
